import { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Mousewheel, Scrollbar } from 'swiper/modules';
import clsx from 'clsx';
import { useResizeDetector } from 'react-resize-detector';
import { ICategoryOption, IOption, ISwatchContent } from '../../types/customizer';
import 'swiper/css';
import 'swiper/css/scrollbar';
import 'swiper/css/free-mode';
import 'swiper/css/mousewheel';

import './sizeFilter.scss';

export interface ISizeFilterProps {
  content: ISwatchContent;
  onChange: (updated: any) => void;
  innerTab?: number;
  isFetching: boolean;
}

const SizeFilter = ({
  content,
  onChange,
  isFetching,
  innerTab = 1,
}: ISizeFilterProps) => {
  const { fieldType } = content;
  const options: Array<IOption> = content.options as Array<IOption>;

  const { width, ref } = useResizeDetector();
  /** One-time layout nudge: repeating on every `width` update retriggers ResizeObserver → setState loop. */
  const widthNudgeDoneRef = useRef(false);
  const [, setIsChanging] = useState('');
  const [, setSwiper] = useState<any>(null);
  const [activeIndex, setActiveIndex] = useState(() => {
    const index = options.findIndex((item) => item.selected);
    return index;
  });

  useEffect(() => {
    if (!isFetching) {
      setIsChanging('');
    }
  }, [isFetching]);

  useEffect(() => {
    const index = options.findIndex((item) => item.selected);
    setActiveIndex(index);
  }, [options]);

  useEffect(() => {
    if (!width || widthNudgeDoneRef.current) return;
    const el = ref.current;
    if (!el) return;
    widthNudgeDoneRef.current = true;
    const w = width;
    el.style.width = `${w + 1}px`;
    const t = globalThis.setTimeout(() => {
      el.style.width = `${w}px`;
    }, 100);
    return () => globalThis.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `ref` from useResizeDetector is not referentially stable; including it retriggers this effect and fights the one-time nudge.
  }, [width]);

  const handleChange = (updatedObj: Record<string, string>) => {
    setIsChanging(Object.values(updatedObj)[0]); // used for deseclecting old one till response recieved
    onChange(updatedObj);
  };

  const handleSizeChange = (index: number) => () => {
    if (index !== null && index !== undefined && index !== activeIndex) {
      setActiveIndex(index);
      const option = options[index];
      handleChange({ [fieldType]: option.sku });
    }
  };

  return (
    <div
      className="tw-flex tw-justify-center tw-pb-2 swatch-filter-border"
      style={{ height: `calc(100% - ${45 * innerTab}px)` }}
    >
      <div className="tw-w-full sm:tw-w-full tw-px-4">
        <div className="tw-mb-2 tw-mt-5 xs:tw-text-center sm:tw-text-left">
          <div className="tw-inline-block xs:tw-w-4/6 sm:tw-w-4/6 tw-text-base">Select your finger size</div>
        </div>
        <div className="tw-overflow-y-auto tw-overflow-x-hidden tw-pb-2" ref={ref}>
          {/*
            1. Removed slideToClickedSlide as it interferes with natural scrolling
            2. Removed grabCursor as it's not needed for scroll behavior
            3. Removed resistance to allow natural scrolling to edges
            4. Enhanced freeMode configuration with momentum settings
          */}
          <Swiper
            onSwiper={setSwiper}
            slidesPerView="auto"
            initialSlide={activeIndex}
            centeredSlides={true}
            slideToClickedSlide={false}
            grabCursor={false}
            resistance={false}
            loop={false}
            cssMode={false}
            spaceBetween={8}
            freeMode={{
              enabled: true,
              momentum: true,
              momentumRatio: 1,
              momentumVelocityRatio: 1
            }}
            scrollbar={{
              hide: false, // makes scrollbar always visible
              draggable: true
            }}
            mousewheel={{
              forceToAxis: true,
              sensitivity: 1
            }}
            modules={[Mousewheel, FreeMode, Scrollbar]}
          >
            {options.map((option: IOption | ICategoryOption, index: number) => (
              <SwiperSlide
                key={index}
                className={clsx('size-slide tw-cursor-pointer', { 'active-slide': index === activeIndex })}
                onClick={handleSizeChange(index)}
              >
                {option.name}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  );
};

export default SizeFilter;
